import type { Address } from '@morpho-org/blue-sdk'
import type { ActionRequirement } from '@morpho-org/morpho-sdk'

import {
  AuthorizationRequestMismatchError,
  type SimulationAuthorization,
  toSimulationAuthorizations,
  UnsupportedOperationError
} from '@morpho-org/evm-simulation'
import { isDefined } from '@morpho-org/morpho-ts'
import { isAddressEqual } from 'viem'

import { UnsupportedRequirementError } from '@/helpers/requirementErrors'

import type { AccountAssetChanges } from './simulate'

/** Review-time sim outcome. `blacklist` must block the op; `error` is bypassable. */
export type SimulationStatus = 'disabled' | 'loading' | 'success' | 'blacklist' | 'error'

/** Picks the verified sim value over the JS-math fallback; flags an unverified outcome (warn, don't block). */
export const resolveSimulatedFinalPosition = ({
  status,
  simulatedValue,
  fallbackPosition
}: {
  status: SimulationStatus
  simulatedValue: bigint | undefined
  fallbackPosition: bigint | undefined
}) => {
  const isVerified = status === 'success' && isDefined(simulatedValue)
  return {
    displayedFinalPosition: isVerified ? simulatedValue : fallbackPosition,
    isUnverifiedFinalPosition: !isVerified && status !== 'disabled' && status !== 'loading'
  }
}

/** User's net delta for `token` from the simulated `assetChanges` (e.g. a Vault V2 share token or an aToken); `undefined` if absent. */
export const selectUserTokenDelta = (
  assetChanges: AccountAssetChanges[] | undefined,
  userAddress: Address,
  token: Address
): bigint | undefined => {
  const accountChanges = assetChanges?.find(entry => isAddressEqual(entry.account, userAddress))
  const tokenChange = accountChanges?.changes.find(change => isAddressEqual(change.token, token))
  return tokenChange?.diff
}

/**
 * Maps `getRequirements` to the SDK's typed preview authorizations, in order: built approve /
 * `setAuthorization` txs and permit / Permit2 / Morpho authorization signatures alike. The SDK
 * prepares each one before the *unsigned* bundle in `preview` mode and validates it (owner =
 * sender, typed-data domains), so nothing is hand-encoded here.
 * @throws UnsupportedRequirementError for a requirement the SDK can't simulate
 */
export const buildSimulationAuthorizations = ({
  requirements,
  chainId,
  from
}: {
  requirements: readonly ActionRequirement[] | undefined
  chainId: number
  from: Address | undefined
}): readonly SimulationAuthorization[] => {
  if (!requirements?.length || !from) return []

  return requirements.flatMap(requirement => {
    try {
      return toSimulationAuthorizations({
        chainId,
        mode: 'preview',
        // Only carried into error contexts; the route pins the actual block.
        blockNumber: 0n,
        owner: from,
        requirements: [requirement]
      })
    } catch (error) {
      if (
        error instanceof UnsupportedOperationError ||
        error instanceof AuthorizationRequestMismatchError
      ) {
        throw new UnsupportedRequirementError(requirement.action.type)
      }
      throw error
    }
  })
}

export const tryBuildSimulationAuthorizations = (
  params: Parameters<typeof buildSimulationAuthorizations>[0]
):
  | {
      simulationAuthorizations: readonly SimulationAuthorization[]
      simulationApprovalError: undefined
    }
  | { simulationAuthorizations: []; simulationApprovalError: UnsupportedRequirementError } => {
  try {
    return {
      simulationAuthorizations: buildSimulationAuthorizations(params),
      simulationApprovalError: undefined
    }
  } catch (error) {
    if (!(error instanceof UnsupportedRequirementError)) throw error
    return { simulationAuthorizations: [], simulationApprovalError: error }
  }
}
